import { useMemo, useState } from 'react';
import { Filter } from 'lucide-react';
import { t, useLanguage, type Language } from '@/app/utils/i18n';
import type { AqlGrowthMeasurement } from '@/app/services/growth-service';

interface GrowthMeasurementsTableProps {
  measurements: AqlGrowthMeasurement[];
  loading?: boolean;
}

type MeasurementFilter = 'all' | 'valid' | 'rejected' | 'lowConfidence' | 'outliers';

const FILTER_OPTIONS: { value: MeasurementFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'all' },
  { value: 'valid', labelKey: 'validOnly' },
  { value: 'rejected', labelKey: 'rejectedOnly' },
  { value: 'lowConfidence', labelKey: 'lowConfidence' },
  { value: 'outliers', labelKey: 'outliers' }
];

const LOW_CONFIDENCE_THRESHOLD = 0.6;

const DATE_LOCALES: Record<Language, string> = {
  pt: 'pt-PT',
  en: 'en-GB',
  fr: 'fr-FR',
  es: 'es-ES',
  de: 'de-DE'
};

const formatTime = (value: string, language: Language) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(DATE_LOCALES[language], {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatNumber = (value: number | null, decimals = 1, unit?: string) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(decimals)}${unit ?? ''}`;
};

const formatPct = (value: number | null) =>
  value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;

const formatRejectionReason = (
  reason: string | null,
  tr: (key: string, params?: Record<string, unknown>) => string
) => {
  switch ((reason ?? '').toLowerCase()) {
    case 'low_confidence':
      return tr('lowConfidence');
    case 'ultrasound_echo_overlap':
      return tr('ultrasoundEchoOverlap');
    case 'partial_body':
      return tr('partialBody');
    default:
      return reason ?? '—';
  }
};

const isOutlier = (item: AqlGrowthMeasurement, median: number | null, iqr: number | null) => {
  if (median === null || iqr === null || item.length_mm === null) return false;
  const lower = median - 1.5 * iqr;
  const upper = median + 1.5 * iqr;
  return item.length_mm < lower || item.length_mm > upper;
};

const computeMedianAndIqr = (values: number[]): { median: number; iqr: number } | null => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const pick = (q: number) => sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(q * sorted.length)))];
  return { median: pick(0.5), iqr: pick(0.75) - pick(0.25) };
};

export function GrowthMeasurementsTable({ measurements, loading }: GrowthMeasurementsTableProps) {
  const language = useLanguage();
  const tr = (key: string, params?: Record<string, unknown>) => t('vision', key, language, params);
  const [filter, setFilter] = useState<MeasurementFilter>('all');

  const summary = useMemo(() => {
    const validLengths = measurements.filter((row) => row.is_valid && row.length_mm !== null).map((row) => row.length_mm as number);
    const stats = computeMedianAndIqr(validLengths);
    return { stats };
  }, [measurements]);

  const filtered = useMemo(() => {
    switch (filter) {
      case 'valid':
        return measurements.filter((row) => row.is_valid);
      case 'rejected':
        return measurements.filter((row) => !row.is_valid);
      case 'lowConfidence':
        return measurements.filter(
          (row) => row.detection_confidence !== null && row.detection_confidence < LOW_CONFIDENCE_THRESHOLD
        );
      case 'outliers':
        return measurements.filter((row) =>
          summary.stats ? isOutlier(row, summary.stats.median, summary.stats.iqr) : false
        );
      default:
        return measurements;
    }
  }, [measurements, filter, summary.stats]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="h-5 w-48 animate-pulse rounded bg-muted/70" />
        <div className="mt-4 h-64 animate-pulse rounded bg-muted/40" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">{tr('recentMeasurements')}</h3>
          <p className="text-xs text-muted-foreground">
            {tr('rawGrowthMeasurements', { table: 'aql_growth_measurements' })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilter(option.value)}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                filter === option.value
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-muted/40 text-muted-foreground hover:text-foreground'
              }`}
            >
              {tr(option.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full text-xs">
          <thead className="bg-muted/40 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">{tr('measuredAt')}</th>
              <th className="px-3 py-2 text-right">{tr('length')}</th>
              <th className="px-3 py-2 text-right">{tr('width')}</th>
              <th className="px-3 py-2 text-right">{tr('area')}</th>
              <th className="px-3 py-2 text-right">{tr('weight')}</th>
              <th className="px-3 py-2 text-right">Profundidade</th>
              <th className="px-3 py-2 text-right">Volume</th>
              <th className="px-3 py-2 text-right">Incerteza</th>
              <th className="px-3 py-2 text-right">{tr('confidence')}</th>
              <th className="px-3 py-2 text-right">{tr('quality')}</th>
              <th className="px-3 py-2 text-center">{tr('valid')}</th>
              <th className="px-3 py-2 text-left">{tr('rejectionReason')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {tr('noMeasurementsMatchFilter')}
                </td>
              </tr>
            ) : (
              filtered.slice(0, 200).map((row) => (
                <tr key={row.id} className="bg-card hover:bg-accent/40">
                  <td className="px-3 py-2 font-mono text-foreground">{formatTime(row.measured_at, language)}</td>
                  <td className="px-3 py-2 text-right font-mono text-foreground">
                    {formatNumber(row.length_mm, 1, ' mm')}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    {formatNumber(row.width_mm, 1, ' mm')}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    {formatNumber(row.area_mm2, 0, ' mm²')}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    {formatNumber(row.estimated_weight_g, 2, ' g')}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">{formatNumber(row.depth_value, 3)}</td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">{formatNumber(row.volume_cm3, 1, ' cm³')}</td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">{row.weight_uncertainty_g == null ? '—' : `±${row.weight_uncertainty_g.toFixed(1)} g`}</td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    {formatPct(row.detection_confidence)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    {formatPct(row.measurement_quality)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span
                      className={`inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${
                        row.is_valid
                          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200'
                          : 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-200'
                      }`}
                    >
                      {row.is_valid ? tr('validLower') : tr('rejectedLower')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {formatRejectionReason(row.rejection_reason, tr)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > 200 ? (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {tr('showingFirstRows', { count: filtered.length })}
        </p>
      ) : null}
    </div>
  );
}
